import * as React from 'react';
import { connect } from 'react-redux';

import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { removeDeliverableAttachments } from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { t } from 'utils/language';
import { insertIf } from 'utils/list';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  filterAttachmentsByType
} from 'utils/deliverables';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentType,
  Subcampaign
} from 'types';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

type OwnProps = {
  canUpload: boolean,
  subcampaign: Subcampaign,
  deliverable: Deliverable,
  attachments?: Array<DeliverableAttachment>,
  attachmentType?: DeliverableAttachmentType,
  onDownloadAttachments?: (a?: string | null) => void,
  onUploadModeToggled?: () => void
};

type DispatchProps = {
  dispatchDeleteFiles: (c: number, b: number, a: Array<DeliverableAttachment>) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      confirmButtonLabel: string,
      onConfirm?: () => void,
      onCancel?: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & DispatchProps;

class DeliverableMediaRowContextMenu extends React.PureComponent<Props> {
  deleteFiles = (attachments: Array<DeliverableAttachment>): void => {
    const {
      subcampaign,
      deliverable,
      dispatchDeleteFiles,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    if (attachments.length > 0) {
      dispatchShowConfirmationModal({
        message: t('deliverable_media.attachment_delete_files_confirmation.message'),
        confirmButtonLabel: t('deliverable_media.attachment_delete_files_confirmation.button_confirm'),
        onConfirm: () =>
          dispatchDeleteFiles(subcampaign.offer.id, deliverable.id, attachments)
            .then(() => dispatchHideConfirmationModal()),
        onCancel: () => dispatchHideConfirmationModal()
      });
    }
  };

  handleDownloadFiles = () => {
    const { attachmentType, onDownloadAttachments } = this.props;

    if (attachmentType && onDownloadAttachments) {
      onDownloadAttachments(attachmentType);
    }
  };

  handleDownloadAllFiles = () => {
    const { onDownloadAttachments } = this.props;

    if (onDownloadAttachments) {
      onDownloadAttachments();
    }
  };

  handleDeleteFiles = () => {
    const { attachments, attachmentType } = this.props;

    if (attachments && attachmentType) {
      this.deleteFiles(filterAttachmentsByType(attachments, attachmentType));
    }
  };

  handleDeleteAllFiles = () => {
    const { attachments } = this.props;

    if (attachments) {
      this.deleteFiles(attachments);
    }
  };

  handleUploadModeToggled = () => {
    const { onUploadModeToggled } = this.props;

    if (onUploadModeToggled) {
      onUploadModeToggled();
    }
  };

  renderContextMenu(): React.ReactNode {
    const { canUpload, deliverable, attachments, attachmentType, onUploadModeToggled } = this.props;
    const allAttachments = attachments || [];
    const targetAttachments = attachmentType ? filterAttachmentsByType(allAttachments, attachmentType) : [];

    if (!deliverable) {
      return null;
    }

    let items = [
      ...insertIf(
        targetAttachments.length > 0,
        {
          icon: 'download-custom-fg',
          text: t('context_menu.download_files_label'),
          onClick: this.handleDownloadFiles
        }
      ),
      ...insertIf(
        canUpload && targetAttachments.length > 0,
        {
          icon: 'trash-custom-fg',
          text: t('context_menu.delete_files_label'),
          onClick: this.handleDeleteFiles
        }
      )
    ];

    if (
      (deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_POST || deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY)
      && allAttachments.length > targetAttachments.length
      && items.length > 0
      && attachmentType
    ) {
      items = [
        {
          type: 'heading',
          text: t(`deliverable_media.context_menu_heading.${deliverable.deliverableType}.${String(attachmentType)}`)
        },
        ...items,
        {
          type: 'separator'
        },
        {
          type: 'heading',
          text: t(`deliverable_media.context_menu_heading.${deliverable.deliverableType}.all`)
        },
        {
          icon: 'download-custom-fg',
          text: t('context_menu.download_all_files_label'),
          onClick: this.handleDownloadAllFiles
        },
        ...insertIf(
          canUpload,
          {
            icon: 'trash-custom-fg',
            text: t('context_menu.delete_all_files_label'),
            onClick: this.handleDeleteAllFiles
          }
        )
      ];
    }

    if (onUploadModeToggled) {
      items = [
        ...items,
        ...insertIf(
          items.length > 0,
          {
            type: 'separator'
          }
        ),
        ...insertIf(
          !canUpload,
          {
            icon: 'upload-custom-fg',
            text: t('context_menu.enter_upload_mode_label'),
            onClick: this.handleUploadModeToggled
          }
        ),
        ...insertIf(
          canUpload,
          {
            icon: 'upload-custom-fg',
            text: t('context_menu.exit_upload_mode_label'),
            onClick: this.handleUploadModeToggled
          }
        )
      ];
    }

    if (items.length === 0) {
      return null;
    }

    return (
      <MoreButtonWithMenu
        width={40}
        contextMenuProps={{ items, zIndex: 100 }}
      />
    );
  }

  render(): React.ReactNode {
    return this.renderContextMenu();
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteFiles: (offerId: number, deliverableId: number, attachments: Array<DeliverableAttachment>) =>
    dispatch(removeDeliverableAttachments(offerId, deliverableId, attachments)),

  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(null, mapDispatchToProps)(DeliverableMediaRowContextMenu);
